// MACHINERY V2 (Stroje) — profile tab bodies: the router between the five tabs.
//
// M2.4 shipped this file as five marked placeholders. M2.5 replaced four of
// them; M2.16d replaced the fifth, so there is no placeholder left:
//   overview        -> PrehladTab             (M2.16d, board 20)
//   serviceReports  -> ServiceReportsTabV2    (M2.5, artboards 1000-1002, 1012)
//   documents       -> DocumentsTabV2         (M2.5, artboards 1003-1005, 1014)
//   specifications  -> SpecificationsTabV2    (M2.5, artboards 1006-1008, 1015)
//   parts           -> PartsTabV2             (M2.5, artboards 1009-1011, 1013)
//
// THE `live` PLACEHOLDER AND ITS "COMING SOON" CARD ARE GONE. The tab it stood
// in is now Prehľad (manager ruling Q1-M2.16, recorded in profileTabContract.ts)
// and is shown for unconnected machines too, so a placeholder would be the
// FIRST thing nearly every user saw on nearly every machine. The dark "feed
// controls" simulator rig in artboard 0601 remains prototype-only and is NOT
// built, ever — ticket §4.2. M2.10's signal ledger, when it lands, mounts as a
// SECTION INSIDE PrehladTab for connected machines, not as a sixth tab.
//
// THE SPECIFICATIONS BRANCH IS NOT A GATE. `buildProfileTabs` (profileTabContract
// .ts) only emits "specifications" when the machine HAS a `catalogModelId`, so
// this component can never be asked for that tab without one. The `if` below is
// a TYPE narrowing (`string | undefined` -> `string`) plus a defensive null
// render, not a second copy of the rule — a second copy is exactly what the
// contract file exists to prevent.
//
// EACH TAB OWNS ITS OWN FETCH — WITH ONE NAMED EXCEPTION, AND IT IS PLUMBED
// RATHER THAN OWNED HERE. There is no prefetch in this file and no shared
// loading state: a tab that is not rendered must not cost a request, and the
// surfaces read different sources. The exception is the SERVICE-REPORT query,
// which TWO tabs render from (Prehľad merges it into its activity list,
// Servisné správy lists it). It is owned ONE level up, by ProfileBody, whose
// header carries the full reasoning — the short version is that an instance
// mounted by a tab can unmount mid-flight and still write the app-wide slot,
// which is a hazard no per-tab ownership can express. This component derives
// nothing from it; it hands the same object to both consumers.
//
// TELEMETRY IS PLUMBED, NOT FETCHED. `telemetry` / `presentation` arrive from
// MachineProfileV2's single poller and single derivation and are passed straight
// through to Prehľad. This component derives nothing.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { Machine } from "../../models/models";
import {
  MachineryV2CardPresentation,
  MachineryV2Telemetry,
} from "../fleet/cardStates";
import PrehladTab from "./prehlad/PrehladTab";
import { MachineryV2ProfileTab } from "./profileTabContract";
import ServiceReportsTabV2 from "./tabs/ServiceReportsTabV2";
import DocumentsTabV2 from "./tabs/DocumentsTabV2";
import SpecificationsTabV2 from "./tabs/SpecificationsTabV2";
import PartsTabV2 from "./tabs/PartsTabV2";
import { SrTabQuery } from "./tabs/useSrTabQuery";

export interface ProfileTabBodyProps {
  tab: MachineryV2ProfileTab;
  machine: Machine;
  telemetry: MachineryV2Telemetry;
  presentation: MachineryV2CardPresentation;
  /** ProfileBody's single `useSrTabQuery` instance — see the header. */
  srQuery: SrTabQuery;
}

const ProfileTabBody: React.FC<ProfileTabBodyProps> = ({
  tab,
  machine,
  telemetry,
  presentation,
  srQuery,
}) => {
  if (tab === "overview") {
    return (
      <PrehladTab
        machineId={machine.id}
        telemetry={telemetry}
        presentation={presentation}
        srQuery={srQuery}
      />
    );
  }
  if (tab === "serviceReports") {
    return <ServiceReportsTabV2 machineId={machine.id} query={srQuery} />;
  }
  if (tab === "documents") return <DocumentsTabV2 machineId={machine.id} />;
  if (tab === "parts") return <PartsTabV2 machineId={machine.id} />;
  if (tab === "specifications") {
    if (!machine.catalogModelId) return null;
    return <SpecificationsTabV2 catalogModelId={machine.catalogModelId} />;
  }
  // Unreachable: the union has five members and all five are handled above. The
  // branch is kept explicit rather than made a fall-through so a reader does not
  // have to prove exhaustiveness to know which tab lands here.
  return null;
};

export default ProfileTabBody;
